/**
 * UserStorage - Unified user identification storage with multiple fallbacks
 * Implements belt-and-suspenders approach: Server cookies → Client cookies → localStorage → IndexedDB
 */

import { getCookie, setCookie, setProfileCookie, getProfileCookie, getCookieConsent } from './cookieHelpers';

export interface UserProfile {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  moon_sign?: string;
  sun_sign?: string;
  rising_sign?: string;
  birth_date?: string;
  birth_time?: string;
  birth_location?: string;
  has_app?: boolean;
  purchase_count?: number;
  last_purchase_date?: string;
  created_at?: string;
}

class UserStorage {
  private static readonly USER_ID_KEY = 'mm_profile_id';
  private static readonly USER_PROFILE_KEY = 'userProfile';
  private static readonly USER_PROFILE_EXPIRY_KEY = 'userProfileExpiry';
  private static readonly CACHE_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  /**
   * Set user ID across all storage mechanisms
   */
  async setUserId(profileId: string): Promise<void> {
    const consent = getCookieConsent();

    // 1. Request server to set HttpOnly cookie (primary, most secure)
    try {
      await fetch('/.netlify/functions/set-user-cookie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profileId })
      });
    } catch (error) {
      console.warn('Failed to set server-side cookie:', error);
    }

    // 2. Set client-side backup cookie (if consent given)
    if (consent.functional) {
      setCookie('mm_uid_backup', profileId, {
        maxAge: 365 * 24 * 60 * 60, // 1 year
        priority: 'High',
        path: '/'
      });
    }

    // 3. Set localStorage (always, as fallback)
    localStorage.setItem(UserStorage.USER_ID_KEY, profileId);

    // 4. Set sessionStorage (for incognito compatibility)
    sessionStorage.setItem(UserStorage.USER_ID_KEY, profileId);

    // stored across all mechanisms
  }

  /**
   * Get user ID from any available storage
   * Priority: Server cookie → Client cookie → localStorage → sessionStorage
   */
  getUserId(): string | null {
    // 1. Try server-side HttpOnly cookie (via API call - can't read directly)
    // This is checked implicitly when making authenticated requests

    // 2. Try client-side backup cookie
    const cookieId = getCookie('mm_uid_backup');
    if (cookieId) {
      return cookieId;
    }

    // 3. Try localStorage
    const localId = localStorage.getItem(UserStorage.USER_ID_KEY);
    if (localId) {
      return localId;
    }

    // 4. Try sessionStorage (incognito mode)
    const sessionId = sessionStorage.getItem(UserStorage.USER_ID_KEY);
    if (sessionId) {
      return sessionId;
    }

    return null;
  }

  /**
   * Set full user profile with caching
   */
  async setUserProfile(profile: UserProfile): Promise<void> {
    const consent = getCookieConsent();

    // 1. Set profile cookie (if consent given)
    if (consent.functional) {
      setProfileCookie(profile, 30); // 30 days
    }

    // 2. Set localStorage with expiry
    localStorage.setItem(UserStorage.USER_PROFILE_KEY, JSON.stringify(profile));
    const expiry = Date.now() + UserStorage.CACHE_DURATION_MS;
    localStorage.setItem(UserStorage.USER_PROFILE_EXPIRY_KEY, expiry.toString());

    // 3. Set sessionStorage (for incognito)
    sessionStorage.setItem(UserStorage.USER_PROFILE_KEY, JSON.stringify(profile));

    // 4. Set user ID separately
    await this.setUserId(profile.id);

    // profile stored
  }

  /**
   * Get full user profile from any available storage
   * Priority: Cookie → sessionStorage → localStorage
   */
  getUserProfile(): UserProfile | null {
    // 1. Try cookie first (most reliable for returning users)
    const cookieProfile = getProfileCookie();
    if (cookieProfile && cookieProfile.id) {
      return cookieProfile;
    }

    // 2. Try sessionStorage (works in incognito)
    const sessionProfile = sessionStorage.getItem(UserStorage.USER_PROFILE_KEY);
    if (sessionProfile) {
      try {
        const profile = JSON.parse(sessionProfile);
        return profile;
      } catch (e) {
        console.error('[UserStorage] Failed to parse session profile:', e);
        sessionStorage.removeItem(UserStorage.USER_PROFILE_KEY);
      }
    }

    // 3. Try localStorage with expiry check
    const cachedProfile = localStorage.getItem(UserStorage.USER_PROFILE_KEY);
    const cachedExpiry = localStorage.getItem(UserStorage.USER_PROFILE_EXPIRY_KEY);

    if (cachedProfile && cachedExpiry) {
      const expiry = parseInt(cachedExpiry, 10);
      if (Date.now() < expiry) {
        try {
          const profile = JSON.parse(cachedProfile);
          return profile;
        } catch (e) {
          console.error('[UserStorage] Failed to parse cached profile:', e);
          this.clearProfileCache();
        }
      } else {
        // cache expired
        this.clearProfileCache();
      }
    }

    return null;
  }

  /**
   * Clear profile cache from all storage mechanisms
   */
  clearProfileCache(): void {
    localStorage.removeItem(UserStorage.USER_PROFILE_KEY);
    localStorage.removeItem(UserStorage.USER_PROFILE_EXPIRY_KEY);
    sessionStorage.removeItem(UserStorage.USER_PROFILE_KEY);
    // cache cleared
  }

  /**
   * Clear all user data (for logout/testing)
   */
  clearAll(): void {
    // Clear localStorage
    localStorage.removeItem(UserStorage.USER_ID_KEY);
    this.clearProfileCache();

    // Clear sessionStorage
    sessionStorage.removeItem(UserStorage.USER_ID_KEY);
    sessionStorage.removeItem(UserStorage.USER_PROFILE_KEY);

    // Clear cookies (client-side only, server-side requires API call)
    if (typeof document !== 'undefined') {
      document.cookie = 'mm_uid_backup=; max-age=0; path=/';
    }

    // all data cleared
  }

  /**
   * Migrate from old localStorage-only storage to new cookie-based storage
   */
  async migrateFromLocalStorage(): Promise<boolean> {
    const existingProfileId = localStorage.getItem(UserStorage.USER_ID_KEY);
    const existingProfile = localStorage.getItem(UserStorage.USER_PROFILE_KEY);

    // Check if we already have cookies (migration complete)
    if (getCookie('mm_uid_backup')) {
      // already migrated
      return false;
    }

    // Migrate user ID if exists
    if (existingProfileId) {
      // migrating user ID
      await this.setUserId(existingProfileId);
    }

    // Migrate profile if exists
    if (existingProfile) {
      try {
        const profile = JSON.parse(existingProfile);
        // migrating profile
        await this.setUserProfile(profile);
        return true;
      } catch (e) {
        console.error('[UserStorage] Failed to migrate profile:', e);
      }
    }

    return false;
  }

  /**
   * Get storage diagnostics for debugging
   */
  getDiagnostics(): {
    hasHttpOnlyCookie: boolean;
    hasClientCookie: boolean;
    hasLocalStorage: boolean;
    hasSessionStorage: boolean;
    userId: string | null;
    profileCached: boolean;
  } {
    return {
      hasHttpOnlyCookie: false, // Can't detect HttpOnly from client
      hasClientCookie: !!getCookie('mm_uid_backup'),
      hasLocalStorage: !!localStorage.getItem(UserStorage.USER_ID_KEY),
      hasSessionStorage: !!sessionStorage.getItem(UserStorage.USER_ID_KEY),
      userId: this.getUserId(),
      profileCached: !!this.getUserProfile()
    };
  }
}

// Export singleton instance
export const userStorage = new UserStorage();
export default UserStorage;
